import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import CustomNavbar from "components/users/CustomNavbar";
import CustomBackButton from "components/general/CustomBackButton";
import logo from "assets/logo.png";
import colorSelectorImage from "assets/colorSelector.png";
import { ChromePicker } from "react-color";
import CustomMultiSelect from "components/general/CustomMultiSelect";
import {
  createAuthenticatedAxiosInstance,
  createProduct,
  fetchCategories,
} from "api/auth";
import { useAuthStore } from "../../../zustand/loginStore";
import { useProductStore } from "../../../zustand/productStore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle,
  faExclamationTriangle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import ColorCard from "./ColorCard";
import ConfirmationModal from "components/general/ConfirmationModal";
import CancelContinueModal from "components/general/CancelContinueModal";
import {
  ColorVariant,
  ProductCreateRequest,
  SizeCreate,
} from "interfaces/IProduct";
import { getColorName } from "utils/imagesAndColors";
import { faQuestionCircle } from "@fortawesome/free-regular-svg-icons";

const AddProduct: React.FC = () => {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const { sizes } = useProductStore();
  const [productName, setProductName] = useState("");
  const [brand, setBrand] = useState("");
  const [colorName, setColorName] = useState("");
  const [color, setColor] = useState("");
  const [categories, setCategories] = useState<
    { value: string; label: string }[]
  >([]);
  const [selectedCategories, setSelectedCategories] = useState<
    { value: string; label: string }[]
  >([]);

  const [colorVariants, setColorVariants] = useState<ColorVariant[]>([]);

  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showColorModal, setShowColorModal] = useState(false);
  const [showCancelContinueModal, setShowCancelContinueModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showResetFormModal, setShowResetFormModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [categoryError, setCategoryError] = useState(false);
  const [colorVariantErrors, setColorVariantErrors] = useState<boolean[]>([]);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const [errorMessage, setErrorMessage] = useState<string>(
    "Hubo un error al crear el producto. Por favor, inténtalo de nuevo."
  );
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  /**
   * Effect hook to load categories when the component mounts
   * @effect
   * @dependencies [axiosInstance]
   */
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await fetchCategories(axiosInstance, {
          per_page: 1000,
        });
        const formattedCategories = response.categories.map((category) => ({
          value: category.id.toString(),
          label: category.name,
        }));
        setCategories(formattedCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    loadCategories();
  }, []);
  /**
   * Handles color change in the color picker.
   * Updates the color state and fetches the corresponding color name.
   * @param {Object} newColor - The new color object from the color picker.
   * @param {string} newColor.hex - The hexadecimal value of the new color.
   */
  const handleColorChange = async (newColor: { hex: string }) => {
    setColor(newColor.hex);
    const colorName = await getColorName(newColor.hex);
    setColorName(colorName);
  };

  /**
   * Adds a new color variant to the product.
   * Initializes sizes for the new color variant if it doesn't already exist.
   * @param {Object} newColor - The new color object to add.
   * @param {string} newColor.color - The color value.
   * @param {string} newColor.name - The name of the color.
   */
  const handleAddColor = (newColor: { color: string; colorName: string }) => {
    if (!colorVariants.some((c) => c.color === color)) {
      const initializedSizes: SizeCreate[] = sizes
        .filter((size) => size.status !== 0)
        .map((size) => ({
          size: size.name,
          price: 0,
          stock: 0,
          description: "",
        }));

      setColorVariants([
        ...colorVariants,
        {
          ...newColor,
          images: [],
          sizes: initializedSizes,
        },
      ]);
    } else {
      setShowColorModal(true);
    }
  };

  /**
   * Deletes a color variant from the product.
   * @param {number} index - The index of the color variant to delete.
   */
  const handleDeleteColorVariant = (index: number) => {
    setColorVariants((prevVariants) =>
      prevVariants.filter((_, i) => i !== index)
    );
  };

  /**
   * Validates the product data before submission.
   * Checks for product name, categories, and color variants with valid sizes.
   * @returns {boolean} True if the product data is valid, false otherwise.
   */
  const validateProductData = () => {
    let isValid = true;
    let errorMessage = "";

    setNameError(false);
    setCategoryError(false);
    setColorVariantErrors(new Array(colorVariants.length).fill(false));

    if (!productName.trim()) {
      isValid = false;
      errorMessage += "El producto debe tener un nombre.\n";
      setNameError(true);
    }

    if (selectedCategories.length === 0) {
      isValid = false;
      errorMessage += "Debe seleccionar al menos una categoría.\n";
      setCategoryError(true);
    }

    if (colorVariants.length === 0) {
      isValid = false;
      errorMessage += "Debe agregar al menos un color.\n";
    } else {
      const newColorVariantErrors = colorVariants.map((variant) => {
        const hasValidSizes = variant.sizes.some(
          (size) => size.price > 0 && size.stock > 0
        );
        if (!hasValidSizes) {
          isValid = false;
          errorMessage += `El color ${variant.color} debe tener al menos una talla con precio y stock.\n`;
        }
        return !hasValidSizes;
      });
      setColorVariantErrors(newColorVariantErrors);
    }

    return isValid;
  };

  /**
   * Effect hook to handle clicks outside the color picker
   * @effect
   * @listens mousedown - Global document event
   * @dependencies [] - Empty dependency array as this only needs to run once on mount
   *
   * The effect:
   * 1. Creates event handler that checks if click was outside color picker
   * 2. Adds mousedown listener to document on mount
   * 3. Removes listener on unmount for cleanup
   *
   * @param {MouseEvent} event - The mouse click event
   * @returns Cleanup function to remove event listener
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(event.target as Node)
      ) {
        setShowColorPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  /**
   * Handles the form submission.
   * Validates the product data and shows the confirmation modal if valid.
   * @param {React.FormEvent} event - The form submission event.
   */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const isValid = validateProductData();

    if (isValid) {
      setShowCancelContinueModal(true);
    }
  };

  /**
   * Confirms the product submission.
   * Creates the product data object and sends it to the server.
   * Shows success or error modal based on the response.
   */
  const confirmSubmit = async () => {
    const productData: ProductCreateRequest = {
      name: productName,
      brand: brand,
      status: 1,
      categories: selectedCategories.map((cat) => parseInt(cat.value)),
      attributes: colorVariants.map((variant) => ({
        color: variant.color,
        colorName: variant.colorName,
        images: variant.images,
        sizes: variant.sizes.filter((size) => size.stock > 0),
      })),
    };

    try {
      const response = await createProduct(axiosInstance, productData);
      if (response.status === 201 || response.status === 200) {
        setShowSuccessModal(true);
      } else {
        setShowErrorModal(true);
      }
    } catch (error: any) {
      console.error("Error creating product:", error);
      
      // Manejar error 422 (validación del backend)
      if (error.response?.status === 422 && error.response?.data?.errors) {
        const errors = error.response.data.errors;
        
        // Verificar si hay errores de descripción
        const descriptionErrors = Object.keys(errors).filter(key => 
          key.includes('description')
        );
        
        if (descriptionErrors.length > 0) {
          setShowValidationErrors(true);
          
          // Extraer información sobre qué colores necesitan descripción
          const colorIndices = descriptionErrors.map(key => {
            const match = key.match(/attributes\.(\d+)\.sizes\.\d+\.description/);
            return match ? parseInt(match[1]) : null;
          }).filter((index): index is number => index !== null);
          
          const uniqueColorIndices = Array.from(new Set(colorIndices));
          const colorNames = uniqueColorIndices.map(index => 
            colorVariants[index]?.colorName || `Color ${index + 1}`
          );
          
          if (colorNames.length > 0) {
            alert(`Por favor, completa la descripción para los siguientes colores:\n${colorNames.join('\n')}`);
          } else {
            alert("Por favor, completa todas las descripciones requeridas para cada color del producto.");
          }
          return;
        }
        
        // Si hay otros errores de validación, mostrar mensaje específico
        const errorMessages = Object.values(errors).flat().join('\n');
        alert(`Error de validación:\n${errorMessages}`);
        return;
      }
      
      // Manejar otros errores HTTP
      if (error.response?.data?.message) {
        setErrorMessage(error.response.data.message);
        setShowErrorModal(true);
        return;
      }
      
      // Para errores de red o desconocidos, mostrar el modal genérico
      setShowErrorModal(true);
    }
  };

  /**
   * Handles the reset form action.
   * Shows the reset form confirmation modal.
   */
  const handleResetForm = () => {
    setShowResetFormModal(true);
  };

  /**
   * Confirms the form reset action.
   * Resets all form fields to their initial values.
   */
  const confirmResetForm = () => {
    setProductName("");
    setBrand("");
    setSelectedCategories([]);
    setShowResetFormModal(false);
    setColorName("");
    setColor("");
    setShowValidationErrors(false);
  };

  /**
   * Handles the back action.
   * Navigates to the products inventory page.
   */
  const handleBack = () => {
    setShowValidationErrors(false);
    navigate("/inventories/products");
  };

  return (
    <>
      <CustomNavbar />
      <CustomBackButton onClick={handleBack} />
      <Container className="px-4 px-md-5" style={{ maxWidth: "1000px" }}>
        <Row className="justify-content-center mb-4">
          <Col xs={12} className="text-center">
            <img
              src={logo}
              alt="Logo"
              className="logo"
              style={{ width: "175px" }}
            />
          </Col>
        </Row>
        <Row className="mb-4">
          <Col className="text-center">
            <h1>Agregar producto</h1>
          </Col>
        </Row>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Nombre del producto</Form.Label>
            <Form.Control
              type="text"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder="Nombre del producto"
              isInvalid={nameError}
            />
            {nameError && (
              <Form.Control.Feedback type="invalid">
                El producto debe tener un nombre.
              </Form.Control.Feedback>
            )}
          </Form.Group>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Categoría</Form.Label>
                <div className={categoryError ? "is-invalid" : ""}>
                  <CustomMultiSelect
                    options={categories}
                    value={selectedCategories}
                    onChange={(newValue) =>
                      setSelectedCategories(
                        newValue as { value: string; label: string }[]
                      )
                    }
                    placeholder="Seleccionar categorías"
                  />
                </div>

                {categoryError && (
                  <div className="text-danger">
                    Debe seleccionar al menos una categoría.
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Marca</Form.Label>
                <Form.Control
                  type="text"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  placeholder="Marca"
                />
              </Form.Group>
            </Col>
          </Row>
          <Form.Group className="mb-3">
            <Form.Label>
              Color:
              <OverlayTrigger
                placement="right"
                overlay={
                  <Tooltip className="custom-tooltip">
                    Selecciona el color en el ciruclo para poder agregarlo a la
                    lista de colores disponibles, dentro podras colocar la
                    cantidad de stock, tallas, descripción, imagenes y el precio
                    del producto
                  </Tooltip>
                }
              >
                <FontAwesomeIcon icon={faQuestionCircle} className="ms-2" />
              </OverlayTrigger>
            </Form.Label>
            <Row className="align-items-center">
              <Col md={6}>
                <Form.Control
                  type="text"
                  value={colorName}
                  onChange={(e) => setColorName(e.target.value)}
                  placeholder="Nombre del color"
                />
              </Col>
              <Col md={1} style={{ marginRight: "-1rem" }}>
                {colorName === "" ? (
                  <Button
                    variant="link"
                    onClick={() => setShowColorPicker(true)}
                    style={{ padding: 0, border: "none" }}
                  >
                    <img
                      src={colorSelectorImage}
                      alt="Color Selector"
                      style={{ cursor: "pointer", height: "2rem" }}
                    />
                  </Button>
                ) : (
                  <Button
                    variant="light"
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    style={{
                      backgroundColor: "white",
                      border: "1px solid white",
                      padding: "0",
                    }}
                  >
                    <FontAwesomeIcon
                      icon={faCircle}
                      style={{
                        height: "2rem",
                        color: color,
                        border: "1px solid black",
                        borderRadius: "50%",
                        verticalAlign: "middle",
                      }}
                    />
                  </Button>
                )}
                {showColorPicker && (
                  <div
                    ref={colorPickerRef}
                    style={{
                      position: "absolute",
                      zIndex: 2,
                      backgroundColor: "white",
                      borderRadius: "5px",
                    }}
                  >
                    <ChromePicker
                      color={color}
                      onChange={handleColorChange}
                      styles={{
                        default: {
                          picker: {
                            backgroundColor: "rgba(251, 229, 132, 0.5)",
                          },
                        },
                      }}
                    />
                  </div>
                )}
              </Col>

              <Col md={3}>
                <Form.Control
                  type="text"
                  value={color}
                  readOnly
                  placeholder="#Hex"
                />
              </Col>
              <Col md={2} style={{ width: "18.33333%" }}>
                <Button
                  variant="primary"
                  className="w-100"
                  onClick={() =>
                    handleAddColor({ color: color, colorName: colorName })
                  }
                  disabled={!colorName || !color}
                >
                  Añadir color
                </Button>
              </Col>
            </Row>
          </Form.Group>
          {colorVariants.map((colorVariant: ColorVariant, index: number) => (
            <ColorCard
              key={colorVariant.color}
              colorVariant={colorVariant}
              onUpdate={(updatedVariant: ColorVariant) => {
                const newColorVariants: ColorVariant[] = [...colorVariants];
                newColorVariants[index] = updatedVariant;
                setColorVariants(newColorVariants);
              }}
              onDelete={() => handleDeleteColorVariant(index)}
              hasError={colorVariantErrors[index]}
              showValidationErrors={showValidationErrors}
            />
          ))}
        </Form>
        <Row className="mt-4 mb-5">
          <Col>
            <Button variant="secondary" onClick={handleResetForm}>
              Restablecer formulario
            </Button>
          </Col>
          <Col className="text-end">
            <Button variant="primary" onClick={handleSubmit}>
              Guardar producto
            </Button>
          </Col>
        </Row>
      </Container>

      <ConfirmationModal
        show={showColorModal}
        onHide={() => setShowColorModal(false)}
        title="Color ya añadido"
        message="Este color ya fue añadido anteriormente. Por favor, intenta con otro color."
        icon={faExclamationTriangle}
        iconColor="text-warning"
      />

      <CancelContinueModal
        show={showCancelContinueModal}
        onHide={() => setShowCancelContinueModal(false)}
        onConfirm={() => {
          setShowCancelContinueModal(false);
          confirmSubmit();
        }}
        title="Atención"
        message="¿Estás seguro de agregar un nuevo producto?"
        icon={faQuestionCircle}
        iconColor="text-warning"
      />

      <CancelContinueModal
        show={showResetFormModal}
        onHide={() => setShowResetFormModal(false)}
        onConfirm={confirmResetForm}
        title="Atención"
        message="¿Estás seguro de restablecer la información? Los datos no guardados no surtirán efecto."
        icon={faExclamationTriangle}
        iconColor="text-danger"
      />

      <ConfirmationModal
        show={showSuccessModal}
        onHide={() => {
          setShowSuccessModal(false);
          navigate("/inventories/products");
        }}
        title="Éxito"
        message="El producto se ha creado exitosamente."
        icon={faCheckCircle}
        iconColor="text-success"
      />

      <ConfirmationModal
        show={showErrorModal}
        onHide={() => setShowErrorModal(false)}
        title="Error"
        message={errorMessage}
        icon={faExclamationTriangle}
        iconColor="text-danger"
      />
    </>
  );
};

export default AddProduct;
